import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { usePermissions } from '../../hooks/usePermissions';
import { usePendingInvitationsCount } from '../../hooks/queries/useMyInvitations';
import { useCompanies } from '../../hooks/queries/useCompanies';
import { useCompanyStore } from '../../stores/useCompanyStore';
import { clearConversationOnLogout } from '../../stores/conversationCleanup';
import {
  FiHome,
  FiMapPin,
  FiBriefcase,
  FiLogOut,
  FiChevronLeft,
  FiChevronRight,
  FiUsers,
  FiSettings,
  FiFileText,
  FiUser,
  FiMessageCircle,
  FiArrowLeft,
  FiMail,
  FiHeart,
  FiBarChart2,
  FiActivity,
} from 'react-icons/fi';
import type { IconType } from 'react-icons';
import { Sparkles } from 'lucide-react';
import logoInbest from '/assets/logos/logo-inbest.png';
import logoIcono from '/assets/logos/logo-icono.png';

export interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface NavigationItem {
  name: string;
  path: string;
  icon: IconType | typeof Sparkles;
  description: string;
  badge?: number | null;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const { logout } = useAuth0();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const { count: pendingInvitationsCount } = usePendingInvitationsCount();
  const { activeCompany } = useCompanyStore();
  const isMemberRole = activeCompany?.role === 'MEMBER';
  // Card lRqCSV1P (M3.4) — el link "Pilotos" se muestra con `admin:pilot`
  // OR OWNER de al menos una company. Backend filtra pilots por membership,
  // así que el link es una "puerta" — no una autorización. Rutas admin
  // (``/pilots/new``, ``/pilots/:id/committee``) usan ``RequireAdminPilot``.
  const { companies } = useCompanies();
  const isOwnerOfAnyCompany = companies.some((c) => c.role === 'OWNER');
  const showPilotsLink = hasPermission('admin:pilot') || isOwnerOfAnyCompany;
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  // Detectar si estamos en móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLogout = () => {
    // Card JoKjdQUR — limpiar conversationId anónimo antes de redirigir
    // a Auth0, para no heredar la sesión al próximo login del navegador.
    clearConversationOnLogout();
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  const navigation: NavigationItem[] = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      icon: FiHome,
      description: 'Panel de control principal',
    },
    // Market Insights (card 6a2dc9e6 W6) — solo OWNER, no MEMBER.
    ...(!isMemberRole ? [{
      name: 'Inteligencia de mercado',
      path: '/dashboard/market-insights',
      icon: FiBarChart2,
      description: 'Dashboard B2B agregado de demanda y perfiles',
    }] : []),
    {
      name: 'Proyectos',
      path: '/projects',
      icon: FiBriefcase,
      description: 'Gestión de proyectos inmobiliarios',
    },
    {
      name: 'Propiedades',
      path: '/properties',
      icon: FiMapPin,
      description: 'Gestión de propiedades',
    },
    {
      // [INBEST:addon] START empresas-nav
      name: 'Mis empresas',
      path: '/companies',
      icon: FiUsers,
      description: 'Gestión de mis empresas',
      // [INBEST:addon] END empresas-nav
    },
    // [INBEST:addon] START invitaciones-nav
    {
      name: 'Invitaciones',
      path: '/invitations',
      icon: FiMail,
      description: 'Gestión de invitaciones',
      badge: pendingInvitationsCount > 0 ? pendingInvitationsCount : null,
    },
    // [INBEST:addon] END invitaciones-nav
    // Card V57jW8x8 — ítem único "Leads" para OWNER y MEMBER. La página
    // decide header / empty-state según el rol y el backend filtra
    // server-side. Histórico: pYObsyo4 había introducido "Mis Leads" para
    // MEMBER, hoy consolidado en `/leads`.
    {
      name: 'Leads',
      path: '/leads',
      icon: FiUsers,
      description: 'Leads de la empresa activa',
    },
    // [INBEST:addon] START admin-empresas - Solo para super-admin (read:all_companies)
    ...(hasPermission('read:all_companies') ? [
      {
        name: 'Administrar Empresas',
        path: '/admin/companies',
        icon: FiSettings,
        description: 'Administración de todas las empresas',
      },
      {
        name: 'Leads del Sistema',
        path: '/admin/leads',
        icon: FiUsers,
        description: 'Todos los leads del sistema — SuperAdmin',
      },
    ] : []),
    // [INBEST:addon] END admin-empresas
    // Card i1YgdN9C — "Cajas y subsidios" visible para OWNER, no para MEMBER.
    // Backend protege la mutación con ADMIN_SEMANTIC_CONFIG (solo OWNER).
    // Antes estaba gated por `read:all_companies` (super-admin only), por eso
    // ningún OWNER lo veía aunque tuviera permisos reales para editar.
    // Card original: uUTiurxO (PR #488).
    ...(!isMemberRole ? [{
      name: 'Cajas y subsidios',
      path: '/admin/cajas',
      icon: FiBriefcase,
      description: 'Curaduria de cajas de compensacion y subsidios VIS',
    }] : []),
    {
      name: 'Chat',
      path: '/investment/chat',
      icon: FiMessageCircle,
      description: 'Asistente de chat para búsqueda inmobiliaria',
    },
    // Card lRqCSV1P (M3.4) — módulo Piloto. Visible con `admin:pilot` OR
    // OWNER de alguna company. Backend filtra pilots por membership; el
    // link es una "puerta" hacia `/pilots` (dashboard).
    ...(showPilotsLink ? [{
      name: 'Pilotos',
      path: '/pilots',
      icon: FiActivity,
      description: 'Seguimiento semanal (IEP) de pilotos comerciales',
    }] : []),
    // vJgEO72n iter#2: acceso visible a "Mis favoritos" desde la nav principal
    {
      name: 'Mis favoritos',
      path: '/favorites',
      icon: FiHeart,
      description: 'Proyectos, modelos y propiedades guardados',
    },
    {
      name: 'Asistente IA Studio',
      path: '/ai-studio',
      icon: Sparkles,
      description: 'Estudio de asistente IA',
    },
    {
      name: 'Asistente de publicación',
      path: '/property-wizard',
      icon: FiFileText,
      description: 'Publicar propiedad con asistente conversacional',
    },
  ];

  const isActiveRoute = (path: string): boolean => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Overlay para móvil */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={onToggle}
        />
      )}

      <div className={`
        fixed left-0 top-0 h-full bg-white shadow-lg border-r border-gray-200
        transition-all duration-300 ease-in-out z-40
        ${isOpen ? 'w-60' : 'w-0'}
        lg:relative lg:translate-x-0 lg:block lg:z-auto
        ${isCollapsed ? 'lg:w-16' : 'lg:w-60'}
        ${isCollapsed ? 'lg:shadow-sm' : 'lg:shadow-lg'}
      `}>

      <div className="flex flex-col h-full">
        {/* Header */}
        <div className={`flex items-center justify-center h-[60px] border-b border-gray-200 bg-white`}>
          <style>{`
            .gradient-bg {
              background: linear-gradient(135deg, #39D0B0 0%, #764ba2 100%);
            }
          `}</style>
          {!isCollapsed ? (
            <div className="hidden lg:flex items-center justify-center">
              <img
                src={logoInbest}
                alt="In-Best"
                className="h-8 sm:h-10 md:h-12 lg:h-14 w-auto"
              />
            </div>
          ) : (
            <div className="hidden lg:flex items-center justify-center space-x-2">
              <img
                src={logoIcono}
                alt="In-Best"
                className="h-10 w-auto"
              />
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="flex items-center justify-center w-6 h-6 rounded hover:bg-gray-100 transition-colors"
                title="Expandir menú"
              >
                <FiChevronRight className="w-3 h-3 text-gray-600" />
              </button>
            </div>
          )}

          <div className="flex items-center space-x-1 lg:space-x-1">
            {/* Botón de colapsar (solo en desktop cuando está expandido) */}
            {!isCollapsed && (
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="hidden lg:flex items-center justify-center w-7 h-7 rounded-lg hover:bg-gray-100 transition-colors"
                title="Colapsar menú"
              >
                <FiChevronLeft className="w-3 h-3 text-gray-600" />
              </button>
            )}

            {/* Botón con logo de inbest (solo en móvil) */}
            <button
              onClick={onToggle}
              className="lg:hidden flex items-center justify-center p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="Abrir/Cerrar menú"
            >
              <img
                src={logoInbest}
                alt="In-Best"
                className="h-8 sm:h-10 md:h-12 lg:h-14 w-auto"
              />
            </button>
          </div>
        </div>

        {/* Navegación */}
        <nav className={`flex-1 p-3 space-y-1 overflow-y-auto scrollbar-hide ${isCollapsed ? 'lg:px-2' : ''}`}>
          {navigation.map((item) => {
            const Icon = item.icon;
            const active = isActiveRoute(item.path);

            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`
                  flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} ${isCollapsed ? 'px-2 py-2' : 'px-3 py-2'} rounded-lg transition-all duration-200 relative
                  ${active
                    ? 'text-indigo-700'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
                title={isCollapsed ? item.name : undefined}
                onClick={() => {
                  // En móvil, cerrar el sidebar después de hacer clic
                  if (isMobile) {
                    onToggle();
                  }
                }}
              >
                <div className="relative">
                  <Icon className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'} ${active ? 'text-indigo-600' : 'text-gray-500'}`} />
                  {/* Badge for collapsed state */}
                  {isCollapsed && item.badge && (
                    <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-red-500 rounded-full">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </div>
                {(!isCollapsed || isMobile) && (
                  <>
                    <span className="font-medium text-sm truncate flex-1">{item.name}</span>
                    {/* Badge for expanded state */}
                    {item.badge && (
                      <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium text-white bg-red-500 rounded-full">
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer con botón de logout */}
        <div className={`p-3 border-t border-gray-200 space-y-1 ${isCollapsed ? 'lg:px-2' : ''}`}>
          <NavLink
            to="/"
            className={`
              flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} w-full ${isCollapsed ? 'px-2 py-2' : 'px-3 py-2'} rounded-lg
              text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-all duration-200 relative
            `}
            title={isCollapsed ? 'Volver al Home' : undefined}
            onClick={() => {
              // En móvil, cerrar el sidebar después de hacer clic
              if (isMobile) {
                onToggle();
              }
            }}
          >
            <FiArrowLeft className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'} text-gray-500`} />
            {(!isCollapsed || isMobile) && (
              <span className="font-medium text-sm truncate flex-1">Volver al Home</span>
            )}
          </NavLink>
          <NavLink
            to="/profile"
            className={`
              flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} w-full ${isCollapsed ? 'px-2 py-2' : 'px-3 py-2'} rounded-lg
              text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-all duration-200 relative
              ${isActiveRoute('/profile') ? 'text-indigo-700' : ''}
            `}
            title={isCollapsed ? 'Mi Perfil' : undefined}
            onClick={() => {
              // En móvil, cerrar el sidebar después de hacer clic
              if (isMobile) {
                onToggle();
              }
            }}
          >
            <FiUser className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'} ${isActiveRoute('/profile') ? 'text-indigo-600' : 'text-gray-500'}`} />
            {(!isCollapsed || isMobile) && (
              <span className="font-medium text-sm truncate flex-1">Mi Perfil</span>
            )}
          </NavLink>
          <div
            onClick={handleLogout}
            className={`
              flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} w-full ${isCollapsed ? 'px-2 py-2' : 'px-3 py-2'} rounded-lg
              text-gray-700 hover:bg-red-50 hover:text-red-700 transition-all duration-200 relative cursor-pointer
            `}
            title={isCollapsed ? 'Cerrar sesión' : undefined}
          >
            <FiLogOut className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'} text-gray-500`} />
            {(!isCollapsed || isMobile) && (
              <span className="font-medium text-sm truncate flex-1">Cerrar Sesión</span>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Sidebar;
